import React, { useCallback, useRef, useState, useEffect } from 'react';

import { useLocalState } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Details/useLocalState';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { checkGoalGenAIEnabled } from 'utils/helpers/featureFlags';

import { MAX_OKR_NAME_LENGTH, MIN_OKR_NAME_LENGTH } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';

import InputLabel from 'uiToolkitV1/InputLabel';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { Tooltip } from 'uiToolkitV1/Tooltip';

/* enabling autosave will trigger onChange on all non error values.  */
export const OKRName = ({
  text = '',
  onChange = () => {},
  isObjective = true,
  enableAutosave = true,
  showLabel = true,
  leftComponent,
}) => {
  const { objectivesName, keyResultsName } = useOrganisationSettings();

  const label = (
    <span>
      A short title that summarizes the{' '}
      {isObjective
        ? modifyString(objectivesName, StringModifier.SENTENCE_CASE)
        : modifyString(keyResultsName, StringModifier.SENTENCE_CASE)}{' '}
      and motivates action
      <sup style={{ top: 0, fontSize: '14px' }}>*</sup>
    </span>
  );

  const placeholderAI = isObjective
    ? `Add a specific ${modifyString(
        objectivesName,
        StringModifier.SENTENCE_CASE
      )}.  Click "Help me Write" for AI assistance`
    : `Add a measurable ${modifyString(
        keyResultsName,
        StringModifier.SENTENCE_CASE
      )}. Click "Help me Write" for AI assistance`;

  const placeholder = isObjective
    ? `Add a specific and relevant ${modifyString(
        objectivesName,
        StringModifier.SENTENCE_CASE
      )} e.g. "Increase user engagement"`
    : `Add a relevant and measurable ${modifyString(
        keyResultsName,
        StringModifier.SENTENCE_CASE
      )}, for e.g. "Increase website traffic by 25%"`;

  const ref = useRef();
  const [localOKRName, setLocalOKRName] = useLocalState(text);

  const [error, setError] = useState('');

  const hasError = error.length > 0;

  const safeOnChange = () => {
    if (localOKRName === text) return;
    if (!hasError) onChange(localOKRName);
  };

  const onKeyDown = (ev) => {
    if (ev.key === 'Enter' && !hasError) {
      ref?.current?.blur();
    }
  };

  const validateAndReturnErrorMessage = useCallback((okrName) => {
    /* check if some error is present. */
    if (okrName?.length < MIN_OKR_NAME_LENGTH) {
      return 'Title is a mandatory field';
    }

    if (okrName?.length > MAX_OKR_NAME_LENGTH) {
      return `Max ${MAX_OKR_NAME_LENGTH} characters`;
    }

    return '';
  }, []);

  const handleLocalValueChange = (ev) => {
    const okrName = ev?.target?.value;

    setLocalOKRName(okrName);

    const errorMessage = validateAndReturnErrorMessage(okrName);

    setError(errorMessage);

    /* save non error inputs to redux as soon user inputs. this is to enable save button */
    if (enableAutosave) {
      onChange(okrName);
    }
  };

  useEffect(() => {
    if (text) {
      const errorMessage = validateAndReturnErrorMessage(text);
      setError(errorMessage);
    }
  }, [text, validateAndReturnErrorMessage]);

  const moveCaretToEnd = useCallback((e) => {
    const input = e.target;
    setTimeout(() => {
      input.setSelectionRange(input.value.length, input.value.length);
      input.scrollLeft = input.scrollWidth;
    }, 0);
  }, []);

  return (
    <InputLabel
      labelText={showLabel ? label : ''}
      containerProps={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0px' }}
      captionText={error}
      captionVariant="error"
    >
      <Tooltip label={localOKRName}>
        <InputV2
          ref={ref}
          isDashed
          value={localOKRName}
          onChange={handleLocalValueChange}
          onKeyDown={onKeyDown}
          onBlur={safeOnChange}
          onFocus={moveCaretToEnd}
          placeholder={checkGoalGenAIEnabled() ? placeholderAI : placeholder}
          padding={checkGoalGenAIEnabled() ? '10px 40px' : undefined}
          textOverflow="ellipsis"
          leftComponent={leftComponent}
        />
      </Tooltip>
    </InputLabel>
  );
};
